if (!customElements.get('selling-plan-widget')) {
  customElements.define(
    'selling-plan-widget',
    class SellingPlanWidget extends HTMLElement {
      variantChangeUnsubscriber = undefined;
      domVariantChangedHandler = undefined;

      constructor() {
        super();
        this.refCache = {};
        this.onChange = this.onChange.bind(this);
        this.syncSellingPlanInputs = this.syncSellingPlanInputs.bind(this);
      }

      get sectionId() {
        return this.getAttribute('data-section-id');
      }

      get productId() {
        return this.getAttribute('data-product-id');
      }

      get shopifySection() {
        return document.querySelector(`#shopify-section-${this.sectionId}`);
      }

      get addToCartForms() {
        // FPS-705: the section can also hold OTHER products' cart-add forms (e.g. complementary
        // products with quick-add enabled) -- scope to forms tagged with this widget's own
        // product, or a same-product form leak/sync could attach our selling_plan to them.
        const forms = this.shopifySection?.querySelectorAll('[action*="/cart/add"]') || [];
        return [...forms].filter((form) => form.dataset.productId === this.productId);
      }

      get priceElement() {
        return this.shopifySection?.querySelector('.f-price');
      }

      get regularPriceElement() {
        return this.shopifySection?.querySelector('.f-price__regular');
      }

      get salePriceElement() {
        return this.shopifySection?.querySelector('.f-price__sale');
      }

      get salePriceValue() {
        return this.salePriceElement?.querySelector('.f-price-item--sale');
      }

      get regularPriceValue() {
        return this.salePriceElement?.querySelector('.f-price-item--regular');
      }

      get regularOnlyPriceValue() {
        return this.regularPriceElement?.querySelector('.f-price-item--regular');
      }

      connectedCallback() {
        this.ensureSellingPlanInputs();
        this.addEventListener('change', this.onChange);

        // Cache original price HTML before any selling-plan price update runs,
        // so we can restore it when the user switches back to one-time purchase.
        this._originalPriceState = {
          priceClass: this.priceElement?.className,
          regularHTML: this.regularPriceElement?.innerHTML,
          saleHTML: this.salePriceElement?.innerHTML,
        };

        this.variantChangeUnsubscriber = window.FoxTheme?.pubsub?.subscribe?.(
          window.FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange,
          (event) => {
            const isSameSection = String(event?.data?.sectionId) === String(this.sectionId);
            if (!isSameSection) return;
            this.onVariantChanged(event?.data?.variant?.id);
          }
        );

        if (!this.variantChangeUnsubscriber) {
          this.domVariantChangedHandler = (event) => {
            this.onVariantChanged(event?.detail?.variant?.id);
          };
          document.addEventListener('variant:changed', this.domVariantChangedHandler);
        }

        this.onVariantChanged(this.getCurrentVariantIdFromPage());
      }

      disconnectedCallback() {
        this.removeEventListener('change', this.onChange);
        this.variantChangeUnsubscriber?.();
        if (this.domVariantChangedHandler) {
          document.removeEventListener('variant:changed', this.domVariantChangedHandler);
          this.domVariantChangedHandler = undefined;
        }
      }

      refs(value) {
        if (this.refCache[value]) return this.refCache[value];
        this.refCache[value] = this.querySelector(`[ref='${value}']`);
        return this.refCache[value];
      }

      get formsForVariant() {
        return Array.from(this.querySelectorAll('section[data-variant-id]'));
      }

      get visibleSellingPlanForm() {
        return this.querySelector(`section[data-variant-id="${this.currentVariantId}"]`);
      }

      get selectedOption() {
        const select = this.visibleSellingPlanForm?.querySelector('select[ref="planSelect"]');
        return select?.selectedOptions?.[0] || null;
      }

      // FPS-677: the customer's actual purchase-option choice (one-time vs autoorder radio),
      // not an assumption -- reading this instead of a hardcoded default is what stops
      // syncSellingPlanInputs from auto-applying the AutoOrder discount before it's selected.
      get currentFrequency() {
        return this.shopifySection?.querySelector('input[name^="frequency-option"]:checked')?.value;
      }

      ensureSellingPlanInputs() {
        const source = this.querySelector('input.selected-selling-plan-id[name="selling_plan"]');
        if (!source) return;

        this.addToCartForms.forEach((form) => {
          // FPS-705: source is form-associated via form="..." (not physically nested in any
          // form), so form.querySelector() alone can't see it -- check .form too, or the main
          // form wrongly gets a redundant clone alongside the already-associated source.
          if (form.querySelector('input[name="selling_plan"]') || source.form === form) return;
          const clone = source.cloneNode();
          // Source's form="..." points at the main PDP form -- cloneNode keeps it, so per HTML
          // spec the clone stays owned by that form even when appended into a different one
          // (e.g. the sticky bar's own form), silently dropping selling_plan from FormData on
          // submit. Drop it so the clone associates via normal DOM nesting instead.
          clone.removeAttribute('form');
          form.appendChild(clone);
        });
      }

      setSellingPlanInputValue(value = '') {
        const sellingPlanValue = String(value || '');
        // FPS-705: must update every selling_plan input tied to THIS product's own forms, not just
        // the widget's own (the source) -- clones appended into other forms (e.g. sticky bar) by
        // ensureSellingPlanInputs live outside the widget and were never getting their value synced,
        // staying stuck empty. Scoped via addToCartForms, not the whole section, so a different
        // product's cart-add form (e.g. complementary quick-add) never gets our plan attached.
        const inputs = [
          ...this.querySelectorAll('input[name="selling_plan"]'),
          ...this.addToCartForms.flatMap((form) => [...form.querySelectorAll('input[name="selling_plan"]')]),
        ];
        if (!inputs.length) return;

        for (const input of inputs) {
          input.value = sellingPlanValue;
        }
      }

      resetSellingPlanInputs() {
        this.refs('planSelect').selectedIndex = 0;
        this.syncSellingPlanInputs(this.currentFrequency);
        this.updatePrice();
      }

      getAvaialableSellingPlanOptions() {
        const select = this.refs('planSelect');
        if (!select) return;

        if (select.value && select.value !== '') return;

        let nearest = null;
        for (let i = 0; i < select.options.length; i++) {
          const option = select.options[i];
          if (option.value && option.value !== '') {
            nearest = option;
            break;
          }
        }
        if (nearest) {
          select.value = nearest.value;
          select.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }

      onVariantChanged(variantId) {
        // If no variantId provided, try to get from page or fallback to first form
        if (!variantId) {
          variantId = this.getCurrentVariantIdFromPage();
          // If still no variantId and only one form exists, use it
          if (!variantId) {
            const forms = this.formsForVariant;
            if (forms.length === 1) {
              variantId = forms[0].getAttribute('data-variant-id');
            }
          }
        }

        // If still no variantId, cannot proceed
        if (!variantId) return;

        this.currentVariantId = String(variantId);
        this.updateSellingPlanFormVisibility();

        // Ensure a valid option is selected for this variant.
        const select = this.visibleSellingPlanForm?.querySelector('select[ref="planSelect"]');
        if (select && select.selectedIndex === -1 && select.options.length) {
          select.selectedIndex = 0;
        }

        this.syncSellingPlanInputs(this.currentFrequency);
        this.updatePrice();
      }

      onChange(event) {
        const select = event.target?.closest?.('select[ref="planSelect"]');
        if (!select) return;
        this.syncSellingPlanInputs(this.currentFrequency);
        this.updatePrice();
      }

      updateSellingPlanFormVisibility() {
        // If no currentVariantId, hide all forms
        if (!this.currentVariantId) {
          this.formsForVariant.forEach((form) => {
            form.classList.add('hidden');
          });
          return;
        }

        this.formsForVariant.forEach((form) => {
          const isCurrent = String(form.getAttribute('data-variant-id')) === String(this.currentVariantId);
          form.classList.toggle('hidden', !isCurrent);
        });
      }

      syncSellingPlanInputs(freq) {
        // FPS-677: no purchase-option radio checked yet (initial load/variant change) --
        // don't force a selling plan or tick AutoOrder until the customer actually picks one.
        if (!freq) {
          this.setSellingPlanInputValue('');
          return;
        }

        const sellingPlanId = freq === 'autoorder' ? this.selectedOption?.dataset?.sellingPlanId || '' : '';
        this.setSellingPlanInputValue(sellingPlanId);

        let checkbox = null;
        if (freq === 'autoorder') {
          checkbox = this.shopifySection.querySelector('.appstle_subscription_radio_wrapper input');
          this.updatePrice();
        } else {
          checkbox = this.shopifySection.querySelector('.appstle_one_time_details_wrapper input');
          this.resetPrice();
        }

        if (checkbox) {
          checkbox.checked = true;
          checkbox.dispatchEvent(new Event('change', { bubbles: true }));
        }
        this.syncAppstleWidget(sellingPlanId);
      }

      syncAppstleWidget(sellingPlanId) {
        const appstleSelect = this.shopifySection?.querySelector('select.appstle_select');
        if (!appstleSelect) return;

        if (!sellingPlanId) return;

        const matchingOption = appstleSelect.querySelector(`option[value="${sellingPlanId}"]`);
        if (!matchingOption) return;

        appstleSelect.value = sellingPlanId;
        appstleSelect.dispatchEvent(new Event('change', { bubbles: true }));
      }

      resetPrice() {
        if (!this._originalPriceState) return;
        if (this.priceElement && this._originalPriceState.priceClass !== undefined) this.priceElement.className = this._originalPriceState.priceClass;
        if (this.regularPriceElement && this._originalPriceState.regularHTML !== undefined) this.regularPriceElement.innerHTML = this._originalPriceState.regularHTML;
        if (this.salePriceElement && this._originalPriceState.saleHTML !== undefined) this.salePriceElement.innerHTML = this._originalPriceState.saleHTML;
      }

      updatePrice() {
        // Don't overwrite the main price while the AutoOrder panel is hidden —
        // that would show the discounted subscription price before the user selects AutoOrder.
        const panel = this.closest('[data-autoorder-panel]');
        if (panel && panel.style.display === 'none') return;

        if (!this.priceElement || !this.regularPriceElement || !this.salePriceElement) return;

        // Per-delivery price always updates, even under MAP — the MAP guard only protects
        // the main product price display, not the subscription frequency cost breakdown.
        const perDeliveryPrice = this.selectedOption?.dataset?.recurringPrice || this.selectedOption?.dataset?.perDeliveryPrice;
        const widgetPriceEl = this.refs('widgetPerDeliveryPrice');
        if (perDeliveryPrice && widgetPriceEl) {
          widgetPriceEl.innerHTML = perDeliveryPrice;
        }

        // MAP-priced products: skip main price updates (price is hidden until cart)
        if (this.priceElement.dataset.mapPriceActive === 'true') return;

        const price = this.selectedOption?.dataset?.variantPrice;
        const compareAtPrice = this.selectedOption?.dataset?.variantCompareAtPrice;
        if (!price) return;

        const hasDiscount = !!compareAtPrice && compareAtPrice !== price;
        this.priceElement.classList.toggle('f-price--on-sale', hasDiscount);

        this.regularPriceElement.classList.toggle('hidden', hasDiscount);
        this.salePriceElement.classList.toggle('hidden', !hasDiscount);

        if (hasDiscount) {
          if (this.regularPriceValue) this.regularPriceValue.innerHTML = `<s>${compareAtPrice}</s>`;
          if (this.salePriceValue) this.salePriceValue.innerHTML = price;
        } else {
          if (this.regularOnlyPriceValue) this.regularOnlyPriceValue.innerHTML = price;
        }
      }

      getCurrentVariantIdFromPage() {
        const selectedVariantNode = document.querySelector('variant-selects [data-selected-variant]');
        const raw = selectedVariantNode?.textContent?.trim();
        if (raw) {
          try {
            const variant = JSON.parse(raw);
            if (variant?.id) return variant.id;
          } catch (error) {
            console.error(error);
          }
        }

        const urlVariant = new URLSearchParams(window.location.search).get('variant');
        if (urlVariant) return urlVariant;

        // Fallback: if only one variant form exists, use it
        const forms = this.formsForVariant;
        if (forms.length === 1) {
          const variantId = forms[0].getAttribute('data-variant-id');
          if (variantId) return variantId;
        }

        return null;
      }
    }
  );
}
